import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { authAxios } from "../../utils/auth";
import { toast } from "sonner";
import { 
  Activity, 
  Zap, 
  Users, 
  DollarSign, 
  AlertTriangle,
  BarChart3,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Settings,
  Save,
  RotateCcw,
  ExternalLink,
  Coins
} from "lucide-react";

export default function TokenManagement() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [usageData, setUsageData] = useState([]);
  const [topUsers, setTopUsers] = useState([]);
  const [modelBreakdown, setModelBreakdown] = useState([]);
  const [agentBreakdown, setAgentBreakdown] = useState([]);
  const [siloBreakdown, setSiloBreakdown] = useState([]);
  const [loopReport, setLoopReport] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [defaults, setDefaults] = useState({});
  const [days, setDays] = useState(7);
  const [showBudgetEditor, setShowBudgetEditor] = useState(false);
  const [editingBudget, setEditingBudget] = useState(null);
  
  // Langfuse integration
  const [langfuseConfig, setLangfuseConfig] = useState(null);
  const [tracedEvents, setTracedEvents] = useState([]);
  const [showTracedEvents, setShowTracedEvents] = useState(false);
  
  // Cost to Credit
  const [c2cValue, setC2cValue] = useState("");
  const [usdCrc, setUsdCrc] = useState("");
  const [c2cSaving, setC2cSaving] = useState(false);

  // Fetch Cost to Credit value
  const fetchC2C = async () => {
    try {
      const res = await authAxios.get('/admin/plog/c2c');
      setC2cValue(res.data.c2c_value?.toString() || "1.0");
      setUsdCrc(res.data.usd_crc?.toString() || "");
    } catch (err) {
      console.error("Failed to fetch C2C:", err);
    }
  };

  // Save Cost to Credit value
  const saveC2C = async () => {
    const numValue = parseFloat(c2cValue);
    if (isNaN(numValue) || numValue < 0) {
      toast.error("Please enter a valid positive number for Credit Multiplier");
      return;
    }
    
    const usdCrcValue = usdCrc ? parseFloat(usdCrc) : null;
    if (usdCrc && (isNaN(usdCrcValue) || usdCrcValue < 0)) {
      toast.error("Please enter a valid positive number for USD to CRC");
      return;
    }
    
    setC2cSaving(true);
    try {
      await authAxios.post('/admin/plog/c2c', { 
        c2c_value: numValue,
        usd_crc: usdCrcValue
      });
      toast.success(`Credit settings updated`);
    } catch (err) {
      toast.error("Failed to save Credit settings");
      console.error("Failed to save C2C:", err);
    } finally {
      setC2cSaving(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch all data in parallel
      const [
        statsRes,
        usageRes,
        topUsersRes,
        modelRes,
        agentRes,
        siloRes,
        loopRes,
        budgetRes,
        defaultsRes,
        langfuseRes,
        tracesRes
      ] = await Promise.all([
        authAxios.get(`/admin/tokens/stats?days=${days}`),
        authAxios.get(`/admin/tokens/usage-over-time?days=${days}`),
        authAxios.get(`/admin/tokens/top-users?days=${days}`),
        authAxios.get(`/admin/tokens/model-breakdown?days=${days}`),
        authAxios.get(`/admin/tokens/agent-breakdown?days=${days}`),
        authAxios.get(`/admin/tokens/silo-breakdown?days=${days}`),
        authAxios.get(`/admin/tokens/loop-detector?days=${days}`),
        authAxios.get(`/admin/tokens/budgets`),
        authAxios.get(`/admin/tokens/budgets/defaults`),
        authAxios.get(`/admin/tokens/langfuse-config`),
        authAxios.get(`/admin/tokens/events-with-traces?days=${days}&limit=30`)
      ]);

      if (statsRes.data.success) setStats(statsRes.data.data);
      if (usageRes.data.success) setUsageData(usageRes.data.data);
      if (topUsersRes.data.success) setTopUsers(topUsersRes.data.data);
      if (modelRes.data.success) setModelBreakdown(modelRes.data.data);
      if (agentRes.data.success) setAgentBreakdown(agentRes.data.data);
      if (siloRes.data.success) setSiloBreakdown(siloRes.data.data);
      if (loopRes.data.success) setLoopReport(loopRes.data.data);
      if (budgetRes.data.success) setBudgets(budgetRes.data.data);
      if (defaultsRes.data.success) setDefaults(defaultsRes.data.data);
      if (langfuseRes.data.success) setLangfuseConfig(langfuseRes.data);
      if (tracesRes.data.success) setTracedEvents(tracesRes.data.data);

    } catch (error) {
      console.error("Error fetching token data:", error);
      toast.error("Failed to load token tracking data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchC2C();
  }, [days]);

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num?.toString() || "0";
  };

  const formatCost = (cost) => {
    return `$${cost?.toFixed(4) || "0.0000"}`;
  };

  const getLangfuseTraceUrl = (traceId) => {
    if (!langfuseConfig?.enabled || !langfuseConfig?.trace_url_template || !traceId) {
      return null;
    }
    return langfuseConfig.trace_url_template.replace("{trace_id}", traceId);
  };

  const handleSaveBudget = async (budget) => {
    try {
      const res = await authAxios.put(`/admin/tokens/budgets`, budget);
      
      if (res.data.success) {
        toast.success(`Budget for ${budget.scope_type} updated`);
        fetchData();
        setEditingBudget(null);
      } else {
        toast.error(res.data.detail || "Failed to update budget");
      }
    } catch (error) {
      toast.error("Failed to update budget");
    }
  };

  const handleResetBudgets = async () => {
    if (!window.confirm("Reset all budgets to defaults?")) return;
    
    try {
      const res = await authAxios.post(`/admin/tokens/budgets/reset`);
      
      if (res.data.success) {
        toast.success("Budgets reset to defaults");
        fetchData();
      }
    } catch (error) {
      toast.error("Failed to reset budgets");
    }
  };

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-divine-gold" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="token-management">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Token Tracking</h2>
          <p className="text-white/60 text-sm">Monitor LLM usage, costs, and budgets</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value))}
            className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
          >
            <option value={7}>Last 7 days</option>
            <option value={14}>Last 14 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchData}
            className="border-white/20"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-white/5 border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <Zap className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-white/60 text-xs">Total Tokens</p>
                <p className="text-xl font-bold text-white">{formatNumber(stats?.total_tokens)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white/5 border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-500/20 rounded-lg">
                <Activity className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-white/60 text-xs">LLM Calls</p>
                <p className="text-xl font-bold text-white">{formatNumber(stats?.total_calls)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white/5 border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/20 rounded-lg">
                <DollarSign className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-white/60 text-xs">Est. Cost</p>
                <p className="text-xl font-bold text-white">{formatCost(stats?.total_cost)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white/5 border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-500/20 rounded-lg">
                <Users className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-white/60 text-xs">Active Users</p>
                <p className="text-xl font-bold text-white">{stats?.unique_users || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Cost to Credit Section */}
      <Card className="bg-white/5 border-white/10">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-medium text-white flex items-center gap-2">
            <Coins className="w-5 h-5 text-divine-gold" />
            Cost to Credit
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label className="block text-white/60 text-sm mb-1">
                Credit Multiplier (c2c)
              </label>
              <input
                type="text"
                value={c2cValue}
                onChange={(e) => setC2cValue(e.target.value)}
                placeholder="1.0"
                className="w-32 bg-divine-black border border-white/20 rounded-lg px-3 py-2 text-white focus:border-divine-gold/50 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-white/60 text-sm mb-1">
                USD to CRC
              </label>
              <input
                type="text"
                value={usdCrc}
                onChange={(e) => setUsdCrc(e.target.value)}
                placeholder="0.0"
                className="w-32 bg-divine-black border border-white/20 rounded-lg px-3 py-2 text-white focus:border-divine-gold/50 focus:outline-none"
              />
            </div>
            <button
              onClick={saveC2C}
              disabled={c2cSaving}
              className="px-4 py-2 bg-divine-gold text-divine-black font-medium rounded-lg hover:bg-divine-gold/90 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              {c2cSaving ? "Saving..." : "Save"}
            </button>
          </div>
          <p className="text-white/40 text-xs mt-2">
            Credit Multiplier converts LLM usage cost to credit value. USD to CRC is the USD conversion rate.
          </p>
        </CardContent>
      </Card>

      {/* Loop Detector Alert */}
      {loopReport.length > 0 && (
        <Card className="bg-red-500/10 border-red-500/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-red-400 text-sm flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              Loop Detection Alerts ({loopReport.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {loopReport.slice(0, 5).map((item, idx) => (
                <div key={idx} className="flex items-center justify-between text-sm bg-black/20 rounded p-2">
                  <span className="text-white/70 font-mono text-xs">{item._id?.slice(0, 8)}...</span>
                  <span className="text-white">{item.calls_count} calls</span>
                  <span className="text-red-400">{formatNumber(item.total_tokens)} tokens</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Model & Agent Breakdown */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Model Breakdown */}
        <Card className="bg-white/5 border-white/10">
          <CardHeader className="pb-2">
            <CardTitle className="text-white text-sm flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-divine-gold" />
              Model Usage
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {modelBreakdown.map((item, idx) => (
                <div key={idx} className="flex items-center justify-between text-sm">
                  <span className="text-white/70">{item._id?.model || "Unknown"}</span>
                  <div className="flex items-center gap-4">
                    <span className="text-white">{formatNumber(item.total_tokens)}</span>
                    <span className="text-white/50 text-xs">{formatCost(item.estimated_cost)}</span>
                  </div>
                </div>
              ))}
              {modelBreakdown.length === 0 && (
                <p className="text-white/40 text-sm">No data yet</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Agent Breakdown */}
        <Card className="bg-white/5 border-white/10">
          <CardHeader className="pb-2">
            <CardTitle className="text-white text-sm flex items-center gap-2">
              <Activity className="w-4 h-4 text-divine-gold" />
              Agent Usage
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {agentBreakdown.map((item, idx) => (
                <div key={idx} className="flex items-center justify-between text-sm">
                  <span className="text-white/70">{item._id || "Unknown"}</span>
                  <div className="flex items-center gap-4">
                    <span className="text-white">{formatNumber(item.total_tokens)}</span>
                    <span className="text-white/50 text-xs">{item.calls_count} calls</span>
                  </div>
                </div>
              ))}
              {agentBreakdown.length === 0 && (
                <p className="text-white/40 text-sm">No data yet</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Memory Silo Breakdown */}
      <Card className="bg-white/5 border-white/10">
        <CardHeader className="pb-2">
          <CardTitle className="text-white text-sm flex items-center gap-2">
            <Zap className="w-4 h-4 text-divine-gold" />
            Memory Silo Injection
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {siloBreakdown.map((item, idx) => (
              <div key={idx} className="bg-black/20 rounded-lg p-3">
                <p className="text-white/60 text-xs truncate">{item._id || "Unknown"}</p>
                <p className="text-white font-bold">{formatNumber(item.total_tokens)}</p>
                <p className="text-white/40 text-xs">{item.injection_count} injections</p>
              </div>
            ))}
            {siloBreakdown.length === 0 && (
              <p className="text-white/40 text-sm col-span-4">No memory injection data yet</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Top Users */}
      <Card className="bg-white/5 border-white/10">
        <CardHeader className="pb-2">
          <CardTitle className="text-white text-sm flex items-center gap-2">
            <Users className="w-4 h-4 text-divine-gold" />
            Top Users by Token Usage
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {topUsers.map((user, idx) => (
              <div key={idx} className="flex items-center justify-between text-sm bg-black/20 rounded p-2">
                <div className="flex items-center gap-2">
                  <span className="text-white/90 font-medium">{user.user_name || "Unknown"}</span>
                  <a 
                    href={`/plog/${user.user_id || user._id}`}
                    className="text-divine-gold/70 hover:text-divine-gold flex items-center gap-1 text-xs"
                    title="View user logs"
                  >
                    <ExternalLink className="w-3 h-3" />
                    logs
                  </a>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-white">{formatNumber(user.total_tokens)} tokens</span>
                  <span className="text-white/50">{user.calls_count} calls</span>
                  <span className="text-green-400 text-xs">{formatCost(user.estimated_cost)}</span>
                </div>
              </div>
            ))}
            {topUsers.length === 0 && (
              <p className="text-white/40 text-sm">No user data yet</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Budget Management */}
      <Card className="bg-white/5 border-white/10">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-white text-sm flex items-center gap-2">
              <Settings className="w-4 h-4 text-divine-gold" />
              Budget Configuration
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleResetBudgets}
                className="border-white/20 text-xs"
              >
                <RotateCcw className="w-3 h-3 mr-1" />
                Reset Defaults
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowBudgetEditor(!showBudgetEditor)}
              >
                {showBudgetEditor ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            {budgets.map((budget, idx) => (
              <div 
                key={idx} 
                className={`bg-black/20 rounded-lg p-3 cursor-pointer hover:bg-black/30 transition ${
                  editingBudget?.scope_type === budget.scope_type ? 'ring-1 ring-divine-gold' : ''
                }`}
                onClick={() => setEditingBudget(budget)}
              >
                <p className="text-divine-gold text-xs font-medium uppercase">{budget.scope_type?.replace("_", " ")}</p>
                <p className="text-white font-bold">{formatNumber(budget.token_limit)} tokens</p>
                <p className="text-white/40 text-xs">{budget.call_limit} calls max</p>
                <div className="flex gap-1 mt-1">
                  <span className="text-xs text-yellow-400">{budget.warn_at}%</span>
                  <span className="text-xs text-orange-400">{budget.degrade_at}%</span>
                  <span className="text-xs text-red-400">{budget.block_at}%</span>
                </div>
                {budget.is_default && <span className="text-xs text-white/30">(default)</span>}
              </div>
            ))}
          </div>

          {/* Budget Editor */}
          {showBudgetEditor && editingBudget && (
            <div className="border-t border-white/10 pt-4 mt-4">
              <h4 className="text-white font-medium mb-3">
                Edit: {editingBudget.scope_type?.replace("_", " ").toUpperCase()}
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-white/60 text-xs">Token Limit</Label>
                  <Input
                    type="number"
                    value={editingBudget.token_limit}
                    onChange={(e) => setEditingBudget({...editingBudget, token_limit: parseInt(e.target.value)})}
                    className="bg-black/20 border-white/10"
                  />
                </div>
                <div>
                  <Label className="text-white/60 text-xs">Call Limit</Label>
                  <Input
                    type="number"
                    value={editingBudget.call_limit}
                    onChange={(e) => setEditingBudget({...editingBudget, call_limit: parseInt(e.target.value)})}
                    className="bg-black/20 border-white/10"
                  />
                </div>
                <div>
                  <Label className="text-white/60 text-xs">Warn At (%)</Label>
                  <Input
                    type="number"
                    value={editingBudget.warn_at}
                    onChange={(e) => setEditingBudget({...editingBudget, warn_at: parseInt(e.target.value)})}
                    className="bg-black/20 border-white/10"
                  />
                </div>
                <div>
                  <Label className="text-white/60 text-xs">Degrade At (%)</Label>
                  <Input
                    type="number"
                    value={editingBudget.degrade_at}
                    onChange={(e) => setEditingBudget({...editingBudget, degrade_at: parseInt(e.target.value)})}
                    className="bg-black/20 border-white/10"
                  />
                </div>
                <div>
                  <Label className="text-white/60 text-xs">Block At (%)</Label>
                  <Input
                    type="number"
                    value={editingBudget.block_at}
                    onChange={(e) => setEditingBudget({...editingBudget, block_at: parseInt(e.target.value)})}
                    className="bg-black/20 border-white/10"
                  />
                </div>
                <div className="flex items-end">
                  <Button
                    onClick={() => handleSaveBudget(editingBudget)}
                    className="bg-divine-gold text-black hover:bg-divine-gold/80"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Save Budget
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Langfuse Traced Events */}
      {langfuseConfig?.enabled && (
        <Card className="bg-white/5 border-white/10">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-white text-sm flex items-center gap-2">
                <ExternalLink className="w-4 h-4 text-divine-gold" />
                Langfuse Traces ({tracedEvents.length})
              </CardTitle>
              <div className="flex items-center gap-2">
                {langfuseConfig?.host && (
                  <a
                    href={langfuseConfig.host}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-divine-gold hover:underline flex items-center gap-1"
                  >
                    Open Langfuse <ExternalLink className="w-3 h-3" />
                  </a>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowTracedEvents(!showTracedEvents)}
                >
                  {showTracedEvents ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          </CardHeader>
          {showTracedEvents && (
            <CardContent>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {tracedEvents.map((event, idx) => {
                  const traceUrl = getLangfuseTraceUrl(event.trace_id);
                  return (
                    <div key={idx} className="flex items-center justify-between text-sm bg-black/20 rounded p-2">
                      <div className="flex items-center gap-3">
                        <span className="text-divine-gold font-medium">{event.agent_name}</span>
                        <span className="text-white/50 text-xs">{event.model}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-white">{formatNumber(event.total_tokens)} tokens</span>
                        <span className="text-green-400 text-xs">{formatCost(event.estimated_cost)}</span>
                        {traceUrl ? (
                          <a
                            href={traceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-1 rounded bg-divine-gold/20 hover:bg-divine-gold/40 transition"
                            title="View in Langfuse"
                          >
                            <ExternalLink className="w-3 h-3 text-divine-gold" />
                          </a>
                        ) : (
                          <span className="text-white/30 text-xs font-mono" title="Trace ID (project not configured)">
                            {event.trace_id?.slice(0, 8)}...
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
                {tracedEvents.length === 0 && (
                  <p className="text-white/40 text-sm text-center py-4">
                    No traced events found. Traces are logged when prompt logging is enabled.
                  </p>
                )}
              </div>
              {!langfuseConfig?.project_id && tracedEvents.length > 0 && (
                <p className="text-amber-400/80 text-xs mt-3">
                  Note: Set LANGFUSE_PROJECT_ID env var to enable direct trace links.
                </p>
              )}
            </CardContent>
          )}
        </Card>
      )}
    </div>
  );
}
